import { useEffect, useState, type FormEvent } from 'react'
import QRCode from 'qrcode'
import { apiPost, type EnrollResponse } from '../lib/api.ts'
import { useRefreshSession } from '../lib/session.ts'
import { AuthLayout, FormError, buttonClass, inputClass } from './AuthLayout.tsx'
import { RecoveryCodes } from './RecoveryCodes.tsx'
import { WebauthnCancelled, registerSecurityKey, webauthnAvailable } from './webauthn.ts'

/**
 * First-factor enrolment. Reached whenever an account has a password but no
 * confirmed factor: a freshly accepted invitation, or an account whose 2FA was
 * just reset. There is no skip — 2FA is mandatory with no grace period (§6c),
 * so this screen is the only thing such a session can do.
 *
 * Three steps, and the third is not optional decoration: §6c says recovery
 * codes are generated AT enrolment. Issuing them here is the only point at
 * which we know the user is present, has just proved possession of a factor,
 * and has not yet come to rely on it.
 */
type Step = 'choose' | 'totp' | 'key' | 'codes'

export function EnrollScreen({ email }: { email: string }) {
  const [step, setStep] = useState<Step>('choose')
  const [factor, setFactor] = useState<EnrollResponse | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [showSecret, setShowSecret] = useState(false)
  const [codes, setCodes] = useState<string[] | null>(null)
  const refresh = useRefreshSession()

  // The TOTP secret is only fetched once that path is chosen. Fetching it on
  // mount would mint a pending factor for every user who goes on to register a
  // security key instead.
  useEffect(() => {
    if (step !== 'totp' || factor) return
    let cancelled = false
    apiPost<EnrollResponse>('/auth/mfa/enroll')
      .then(async (enrolled) => {
        if (cancelled) return
        setFactor(enrolled)
        // Rendered client-side from the URI the server returned, so the secret
        // is not additionally round-tripped as an image.
        setQr(
          await QRCode.toDataURL(enrolled.otpauth_uri, {
            width: 200,
            margin: 1,
            errorCorrectionLevel: 'M',
          }),
        )
      })
      .catch(() => {
        if (!cancelled) setError('Could not start enrolment. Reload and try again.')
      })
    return () => {
      cancelled = true
    }
  }, [step, factor])

  /**
   * The factor now exists and this session is satisfied, so recovery codes can
   * be issued — the endpoint requires a full session precisely so a
   * half-authenticated one can never mint a spare key to the account.
   */
  async function issueCodes() {
    try {
      const result = await apiPost<{ codes: string[] }>('/auth/recovery-codes')
      setCodes(result.codes)
      setStep('codes')
    } catch {
      // Not fatal: the factor is enrolled and the account works. Codes can be
      // generated later from the security screen, so this must not trap anyone
      // on an enrolment page they have already completed.
      await refresh()
    }
  }

  async function confirmTotp(event: FormEvent) {
    event.preventDefault()
    if (!factor) return
    setBusy(true)
    setError(null)
    try {
      await apiPost('/auth/mfa/confirm', { factor_id: factor.factor_id, code })
      await issueCodes()
    } catch {
      setError('That code was not accepted. Check your device clock and try the next code.')
      setBusy(false)
    }
  }

  async function enrolKey() {
    setBusy(true)
    setError(null)
    try {
      await registerSecurityKey('Security key')
      await issueCodes()
    } catch (caught) {
      // A dismissed prompt is not a failure worth a message — the browser
      // cannot tell us whether they cancelled or presented the wrong key, and
      // saying the wrong one would be worse than saying nothing.
      setError(
        caught instanceof WebauthnCancelled
          ? null
          : 'That key could not be registered. Try again, or use an authenticator app.',
      )
      setBusy(false)
    }
  }

  if (step === 'codes' && codes) {
    return (
      <AuthLayout title="Save your recovery codes" subtitle="Shown once, and only now.">
        <RecoveryCodes codes={codes} onDone={() => void refresh()} doneLabel="Continue to Blocwork" />
      </AuthLayout>
    )
  }

  if (step === 'choose') {
    return (
      <AuthLayout
        title="Set up two-factor authentication"
        subtitle={`Required for every account. Signed in as ${email}`}
      >
        <div className="space-y-3">
          <button type="button" onClick={() => setStep('totp')} className={buttonClass}>
            Use an authenticator app
          </button>
          <button
            type="button"
            disabled={!webauthnAvailable()}
            onClick={() => {
              setStep('key')
              void enrolKey()
            }}
            className="w-full rounded-md border border-line px-3 py-2 text-sm font-semibold text-ink hover:border-brand-400 disabled:opacity-50"
          >
            Use a security key or passkey
          </button>
          {!webauthnAvailable() ? (
            <p className="text-xs text-ink-muted">
              This browser does not support security keys.
            </p>
          ) : null}
        </div>
        <p className="mt-4 text-xs text-ink-muted">
          You can add the other kind afterwards, and you should. If you are the only
          administrator, nobody can reset yours for you.
        </p>
      </AuthLayout>
    )
  }

  if (step === 'key') {
    return (
      <AuthLayout title="Register your security key" subtitle={`Signed in as ${email}`}>
        <p className="text-sm text-ink-muted">
          {busy
            ? 'Follow your browser’s prompt — touch your key, or approve the passkey.'
            : 'Ready when you are.'}
        </p>
        <FormError message={error} />
        <button type="button" disabled={busy} onClick={() => void enrolKey()} className={`${buttonClass} mt-4`}>
          {busy ? 'Waiting for your key…' : 'Try again'}
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null)
            setStep('totp')
          }}
          className="mt-3 w-full text-xs text-ink-muted underline underline-offset-2 hover:text-ink"
        >
          Use an authenticator app instead
        </button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Set up your authenticator app"
      subtitle={`Required for every account. Signed in as ${email}`}
    >
      {qr ? (
        <div className="flex flex-col items-center">
          {/* White plate so the code stays scannable in dark mode, where the
              surface would otherwise invert the quiet zone. */}
          <img src={qr} alt="QR code for your authenticator app" width={200} height={200} className="rounded-md bg-white p-2" />
          <button
            type="button"
            onClick={() => setShowSecret((v) => !v)}
            className="mt-3 text-xs text-ink-muted underline underline-offset-2 hover:text-ink"
          >
            {showSecret ? 'Hide setup key' : 'Can’t scan? Enter a key instead'}
          </button>
          {showSecret && factor ? (
            <code className="mt-2 rounded bg-surface-sunken px-2 py-1 font-mono text-xs break-all text-ink">
              {factor.secret}
            </code>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-ink-muted">Preparing…</p>
      )}

      <form onSubmit={confirmTotp} className="mt-5 space-y-3">
        <div>
          <label htmlFor="code" className="mb-1 block text-sm font-medium text-ink">
            Enter the 6-digit code to confirm
          </label>
          <input
            id="code"
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className={`${inputClass} text-center font-mono text-lg tracking-[0.4em]`}
          />
        </div>

        <button type="submit" disabled={busy || !factor || code.length < 6} className={buttonClass}>
          {busy ? 'Confirming…' : 'Confirm and continue'}
        </button>
      </form>

      <FormError message={error} />

      {webauthnAvailable() ? (
        <button
          type="button"
          onClick={() => {
            setError(null)
            setStep('key')
            void enrolKey()
          }}
          className="mt-4 w-full text-xs text-ink-muted underline underline-offset-2 hover:text-ink"
        >
          Use a security key instead
        </button>
      ) : null}
    </AuthLayout>
  )
}
